/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteResult component
 */

import { Box, Image as ChakraImage, Flex, useTheme } from '@chakra-ui/react'
import type { ChakraZ4Type } from '@zcode-ui/chakra-themes'
import type { AutocompleteApiResult } from '@zcode/bizos'
import { PropertyFeatures } from '@zcode/enums'
import { buildSrc } from '@zcode/image-cdn-utils'
import { mediaUrl } from '@zcode/nr-utils'
import React from 'react'
import { useDispatch } from 'react-redux'
import { Size } from '../../../../shared/bizos/media'
import type {
  AutocompleteOrigin,
  AutocompleteTheme,
} from '../../../../shared/components/Autocomplete/constants'
import {
  addResultToPreviousSearches,
  getInputValue,
} from '../../../../shared/components/Autocomplete/helpers'
import { trackResultClick } from '../../../../shared/components/Autocomplete/module/thunks/analytics'
import { trackAutocompleteResultClick } from '../../../../shared/modules/traktor/thunks'
import { a11yButtonHandler } from '../../../../shared/utils/a11y'
import { useAutocompleteContext } from '../AutocompleteContext'
import { AUTOCOMPLETE_BUILDING } from '../constants'
import { getIdFromOrigin } from '../helpers'
import { getAutocompleteResultIcon } from '../helpers/getAutocompleteResultIcon'
import { getAutocompleteResultText } from '../helpers/getAutocompleteResultText'
import RecentIcon from '../icons/Recent_blue.svg'
import BuildingIcon from '../icons/UnpaidBuilding.svg'

interface ImageProps {
  color: string
  imgSrc: string | false | 0 | null | undefined
  isPrevSearch: boolean
  type: AutocompleteApiResult['type']
  showBuildingImg: boolean
}

const Image = ({ imgSrc, isPrevSearch, type, color, showBuildingImg }: ImageProps) => {
  if (imgSrc && showBuildingImg && !isPrevSearch) {
    return <BuildingImage imgSrc={imgSrc} />
  }

  return isPrevSearch ? (
    <ChakraImage height="20px" src={RecentIcon} width="20px" />
  ) : (
    getAutocompleteResultIcon(type)
  )
}

const BuildingImage = ({ imgSrc }: Pick<ImageProps, 'imgSrc'>) => {
  return imgSrc ? (
    <ChakraImage height="40px" src={imgSrc} width="40px" />
  ) : (
    <ChakraImage height="20px" src={BuildingIcon} width="20px" />
  )
}

export interface AutocompleteResultProps {
  dispatchMixpanelAutocompleteResultClick?: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  index: number
  fillInputOnSelect: boolean | undefined
  isLastPrevSearchItem?: boolean
  isMapListNav?: boolean
  isPrevSearch: boolean
  onSelectAutocompleteResult: (result: AutocompleteApiResult) => void
  origin?: AutocompleteOrigin
  resetForm: () => void
  result: AutocompleteApiResult
  setFieldValue: (field: 'searchTerm', value: string, validate?: boolean | undefined) => void
  theme: AutocompleteTheme
}

export const AutocompleteResult = ({
  dispatchMixpanelAutocompleteResultClick,
  index,
  isLastPrevSearchItem,
  isMapListNav,
  isPrevSearch,
  fillInputOnSelect,
  onSelectAutocompleteResult,
  origin,
  resetForm,
  result,
  setFieldValue,
  theme,
}: AutocompleteResultProps) => {
  const dispatch = useDispatch()
  const {
    keystrokes,
    hoveredIndex,
    predictionResults,
    searchTerm,
    setIsFocused,
    setForceContextualMenuOpen,
    setHoveredIndex,
    setPredictionResults,
    setSearchTerm,
  } = useAutocompleteContext()
  const { colors } = useTheme<ChakraZ4Type>()
  const { features, imageId, type } = result

  const onMouseDown = () => {
    dispatch(
      trackAutocompleteResultClick({ result, index, searchTerm, allResults: predictionResults })
    )
    dispatchMixpanelAutocompleteResultClick?.({ result, index })
    trackResultClick({ index, keystrokes, origin, predictionResults, result, searchTerm })
    addResultToPreviousSearches(result)
    onSelectAutocompleteResult(result)
    setForceContextualMenuOpen(false)
    setIsFocused(false)
    setPredictionResults(null)
    setSearchTerm('')
    resetForm()
  }
  const onMouseDownAndFillInput = () => {
    if (result) {
      const value = getInputValue(result)
      trackResultClick({ index, keystrokes, origin, predictionResults, result, searchTerm })
      addResultToPreviousSearches(result)
      onSelectAutocompleteResult(result)
      setFieldValue('searchTerm', value)
      setForceContextualMenuOpen(false)
      setIsFocused(false)
    }
  }
  const onMouseEnter = () => {
    setHoveredIndex(index)
  }
  const onMouseLeave = () => setHoveredIndex(-1)

  const { primaryText, secondaryText } = getAutocompleteResultText(result)
  const imgUrl = imageId && mediaUrl(imageId, Size.TINY)
  const imgSrc =
    imgUrl &&
    buildSrc(imgUrl, {
      fit: 'clip',
      h: 200,
      w: 200,
    })
  const showBuildingImg =
    type === AUTOCOMPLETE_BUILDING && features?.includes(PropertyFeatures.DIRECT_DEAL)

  const getTextMatch = (result: string, searchTerm: string) => {
    const lowerCaseResult = result.toLowerCase()
    if (searchTerm && lowerCaseResult?.includes(searchTerm)) {
      const startOfSearchTerm = lowerCaseResult.indexOf(searchTerm)
      const endOfSearchTerm = startOfSearchTerm + searchTerm.length

      const firstPart = result.substr(0, startOfSearchTerm)
      const match = result.substr(startOfSearchTerm, searchTerm.length)
      const thirdPart = result.substr(endOfSearchTerm, result.length)
      return (
        <>
          {firstPart}
          <b>{match}</b>
          {thirdPart}
        </>
      )
    }
    return result
  }

  const primaryTextMatch = getTextMatch(primaryText, searchTerm)
  const secondaryTextMatch = secondaryText ? getTextMatch(secondaryText, searchTerm) : null
  const hoverStyles = {
    backgroundColor: 'gray.50',
    borderLeftColor: 'pink.600',
    borderLeftStyle: 'solid',
    borderLeftWidth: '2px',
    cursor: 'pointer',
    paddingLeft: '14px',
  } as const

  const lastPrevSearchItemStyles = {
    borderBottomColor: 'gray.150',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
  } as const

  return (
    <Flex
      _hover={hoverStyles}
      aria-selected={index === hoveredIndex}
      gap={2}
      id={`${getIdFromOrigin(origin)}-results-${index}`}
      marginY={0}
      onClick={fillInputOnSelect ? onMouseDownAndFillInput : onMouseDown}
      onKeyDown={
        fillInputOnSelect
          ? a11yButtonHandler(onMouseDownAndFillInput)
          : a11yButtonHandler(onMouseDown)
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      paddingX={4}
      paddingY={2}
      role="option"
      sx={{
        svg: {
          height: isPrevSearch ? '20px' : '14px',
          width: isPrevSearch ? '20px' : '14px',
        },
      }}
      tabIndex={-1}
      {...(index === hoveredIndex && hoverStyles)}
      {...(isLastPrevSearchItem && lastPrevSearchItemStyles)}
    >
      <Flex
        alignItems="center"
        backgroundColor={isPrevSearch ? 'neutral.100' : 'gray.100'}
        borderRadius="md"
        height="40px"
        justifyContent="center"
        overflow="hidden"
        width="40px"
        minWidth="40px"
      >
        <Image
          color={colors.neutral['650']}
          imgSrc={imgSrc}
          isPrevSearch={isPrevSearch}
          showBuildingImg={showBuildingImg}
          type={result.type}
        />
      </Flex>
      <Flex alignItems="start" direction="column" justifyContent="center" w="100%">
        <Box<'div'>
          color={isPrevSearch ? 'blue.800' : 'gray.950'}
          fontSize={{ base: 'md', lg: 'sm' }}
          fontWeight="medium"
          margin={0}
          padding={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="calc(100% - 48px)"
        >
          {primaryTextMatch}
        </Box>
        <Box<'div'>
          color={isPrevSearch ? 'blue.800' : 'gray.600'}
          fontSize="xs"
          margin={0}
          padding={0}
        >
          {secondaryTextMatch}
        </Box>
      </Flex>
    </Flex>
  )
}
